import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

import * as echarts from 'echarts/core';
import '../config/echarts_theme';

import { BarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  TransformComponent,
  ToolboxComponent,
} from 'echarts/components';

// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
// Note that including the CanvasRenderer or SVGRenderer is a required step
import { CanvasRenderer } from 'echarts/renderers';
import { service } from '@ember/service';

// Register the required components
echarts.use([
  BarChart,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
  LegendComponent,
  ToolboxComponent,
]);

function cleanup(instance) {
  instance?.chart?.dispose?.();
}

export default class BarChartModifier extends Modifier {
  @service organisation;

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  modify(element, [data], options) {
    this.options = options;

    if (this.chart == undefined) {
      this.setupChart(element);
      this.registerOnClick();
    }
    if (data != undefined) {
      this.updateChart(data, options);
    } else this.updateChart([], options);
  }

  get height() {
    const { height } = this.options;
    return height ?? 600;
  }

  get clickHandler() {
    const { onClick } = this.options;
    return onClick;
  }

  setupChart(element) {
    this.chart = echarts.init(element, 'producthub', {
      height: this.height,
    });

    this.chart.setOption(this.chartOptions);
  }

  get chartOptions() {
    let options = {
      tooltip: {
        // trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        valueFormatter: this.currencyFormatter,
      },
      toolbox: {
        show: false,
        orient: 'horizontal',
        left: 'left',
        top: 'top',
        feature: {
          // mark: { show: true },
          magicType: { show: true, type: ['stack'] },
          saveAsImage: { show: true },
        },
      },
      xAxis: {
        type: 'category',
        data: ['Investment', 'Return', 'Impact'],
        triggerEvents: true,
      },
      yAxis: {
        axisLabel: {
          formatter: this.currencyFormatter,
        },
      },
    };

    return options;
  }

  registerOnClick() {
    const handler = this.clickHandler;
    if (handler != undefined)
      this.chart.on('click', { targetType: 'axisLabel' }, handler);
  }

  updateChart(data) {
    this.chart.setOption(
      {
        ...this.chartOptions,
        ...this.datasetToSeries(data),
      },
      true,
    );
  }

  datasetToSeries(source) {
    let series = source.slice(1);
    const options = { series: [] };
    if (source.length == 0) return options;
    const name_index = source[0].indexOf('title');
    const stack_index = source[0].indexOf('source');
    const bon_investment_index = source[0].indexOf('bon_investment');
    const bon_returns_index = source[0].indexOf('bon_returns');
    const bon_value_index = source[0].indexOf('bon_value');
    const id_index = source[0].indexOf('id');

    series.forEach((serie) => {
      if (!serie[stack_index]) return;
      if (
        !serie[bon_investment_index] &&
        !serie[bon_returns_index] &&
        !serie[bon_value_index]
      )
        return;
      options.series.push({
        type: 'bar',
        name: serie[name_index],
        stack: 'single', //serie[stack_index],
        data: [
          serie[bon_investment_index],
          serie[bon_returns_index],
          serie[bon_value_index],
        ],
        id: serie[id_index],
      });
    });

    return options;
  }

  currencyFormatter = new Intl.NumberFormat(
    this.organisation.locale === 'en-qpq' ? 'en-US' : this.organisation.locale,
    {
      style: 'currency',
      currency: this.organisation.locale === 'en-US' ? 'USD' : 'EUR',
      notation: 'compact',
      maximumFractionDigits: 0, // (causes 2500.99 to be printed as €2,501)
    },
  ).format;
}
